'use client';
import { useEffect, useState } from 'react';
import { getMyShifts } from 'features/shift/shiftApi';
import { Container, Row, Col, Alert, Spinner } from 'react-bootstrap';
import Badge from 'components/base/Badge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faClipboardList,
  faCalendarCheck,
  faTimes,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import PostedShiftsCard from './components/PostedShiftsCard';
import FilledVsOpenCard from './components/FilledVsOpenCard';

interface Shift {
  id: number;
  date: string;
  start_time: string;
  end_time: string;
  location: string;
  address?: string;
  pay_rate: number;
  required_locums: number;
  booked_locums_count: number;
  status: string;
  locum_role: string;
  summary?: string;
}

const PharmacistDashboardPage = () => {
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchShifts = async () => {
    try {
      setLoading(true);
      const data = await getMyShifts();
      setShifts(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShifts();
  }, []);

  // Calculate summary statistics
  const summary = shifts.reduce(
    (acc, shift) => {
      acc.total += 1;
      acc.open += shift.status === 'open' ? 1 : 0;
      acc.closed += shift.status === 'closed' ? 1 : 0;
      acc.fullyBooked +=
        shift.booked_locums_count >= shift.required_locums ? 1 : 0;
      return acc;
    },
    { total: 0, open: 0, closed: 0, fullyBooked: 0 }
  );

  return (
    <Container className="py-2 py-lg-4">
      {/* Header Section */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
            <div>
              <p className="text-body-tertiary text-uppercase fs-10 fw-bold mb-1">
                Pharmacy Workspace
              </p>
              <h2 className="text-body-emphasis mb-1">Dashboard</h2>
              <p className="mb-0 text-body-tertiary fs-9">
                Manage and track your posted shifts and bookings.
              </p>
            </div>
          </div>
        </Col>
      </Row>

      {/* Error Alert */}
      {error && (
        <Row className="mb-4">
          <Col>
            <Alert variant="danger" className="mb-0">
              <FontAwesomeIcon icon={faTimes} className="me-2" />
              {error}
            </Alert>
          </Col>
        </Row>
      )}

      {/* Summary Stats */}
      {!loading && (
        <Row className="g-3 mb-4">
          {/* Total */}
          <Col sm={6} lg={3}>
            <div className="p-3 rounded-3 bg-body border border-translucent h-100 transition-all hover-card">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <small className="text-body-tertiary text-uppercase fw-bold">
                  Total Shifts
                </small>
                <FontAwesomeIcon
                  icon={faClipboardList}
                  className="text-secondary"
                />
              </div>

              <h3 className="mb-0 fw-semibold text-body-emphasis">
                {summary.total}
              </h3>
            </div>
          </Col>

          {/* Open */}
          <Col sm={6} lg={3}>
            <div className="p-3 rounded-3 bg-body border border-translucent h-100 transition-all hover-card">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <small className="text-body-tertiary text-uppercase fw-bold">
                  Open Shifts
                </small>
                <FontAwesomeIcon icon={faCalendarCheck} className="text-info" />
              </div>

              <h3 className="mb-0 fw-semibold text-info">{summary.open}</h3>
            </div>
          </Col>

          {/* Fully Booked */}
          <Col sm={6} lg={3}>
            <div className="p-3 rounded-3 bg-body border border-translucent h-100 transition-all hover-card">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <small className="text-body-tertiary text-uppercase fw-bold">
                  Fully Booked
                </small>
                <FontAwesomeIcon
                  icon={faCheckCircle}
                  className="text-success"
                />
              </div>

              <h3 className="mb-0 fw-semibold text-success">
                {summary.fullyBooked}
              </h3>
            </div>
          </Col>

          {/* Closed */}
          <Col sm={6} lg={3}>
            <div className="p-3 rounded-3 bg-body border border-translucent h-100 transition-all hover-card">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <small className="text-body-tertiary text-uppercase fw-bold">
                  Closed Shifts
                </small>
                <FontAwesomeIcon icon={faTimes} className="text-danger" />
              </div>

              <h3 className="mb-0 fw-semibold text-danger">{summary.closed}</h3>
            </div>
          </Col>
        </Row>
      )}
      {/* Main Content Sections */}
      <Row className="g-4">
        <Col lg={6}>
          <PostedShiftsCard shifts={shifts} loading={loading} />
        </Col>
        <Col lg={6}>
          <FilledVsOpenCard shifts={shifts} loading={loading} />
        </Col>
      </Row>
    </Container>
  );
};

export default PharmacistDashboardPage;
